import React from 'react';
import {
  StyleSheet, Document, Page, View, Text
} from '@react-pdf/renderer';
import { toBrazilDateTime } from '../../../components/mask';

export interface CallMonthExportRow {
  Data: string;
  Cliente: string;
  Condutor: string;
  Destinos: number | string;
  Retorno: string;
  Pagamento: string;
  Valor: string;
  Status: string;
  Canal: string;
}

interface OwnProps {
  data: CallMonthExportRow[];
  fileName: string;
  filters: {
    subsidiary: string;
    period: string;
  };
  totalRecords: number;
  truncated: boolean;
}

export default function CallsMonthPrint({
  data,
  fileName,
  filters,
  totalRecords,
  truncated
}: OwnProps) {
  const styles = StyleSheet.create({
    body: {
      padding: 12,
      paddingBottom: 50
    },
    table: {
      width: 'auto',
      borderStyle: 'solid',
      borderColor: '#bfbfbf',
      borderWidth: 1,
      borderRightWidth: 0,
      borderBottomWidth: 0
    },
    tableRow: {
      flexDirection: 'row'
    },
    colHeader: {
      borderStyle: 'solid',
      borderColor: '#bfbfbf',
      borderBottomColor: '#000',
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0,
      padding: 3
    },
    col: {
      borderStyle: 'solid',
      borderColor: '#bfbfbf',
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0,
      padding: 2
    },
    tableCellHeader: {
      fontSize: 8,
      fontWeight: 500,
      textAlign: 'center'
    },
    tableCell: {
      fontSize: 7
    },
    header: {
      fontSize: 12,
      marginBottom: 8,
      textAlign: 'center',
      color: 'grey'
    },
    filters: {
      fontSize: 9,
      marginBottom: 10,
      textAlign: 'center',
      color: 'darkgrey'
    },
    total: {
      fontSize: 9,
      marginTop: 8,
      textAlign: 'right',
      fontWeight: 'bold'
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 9,
      bottom: 20,
      left: 0,
      right: 0,
      textAlign: 'center',
      color: 'grey'
    }
  });

  const widths: Record<string, string> = {
    Data: '12%',
    Cliente: '18%',
    Condutor: '16%',
    Destinos: '8%',
    Retorno: '8%',
    Pagamento: '10%',
    Valor: '9%',
    Status: '10%',
    Canal: '9%'
  };

  const firstRow = data[0] || {};
  const rowKeys = Object.keys(firstRow);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.body}>
        <Text style={styles.header} fixed>
          Relatório detalhado de chamadas por mês
          {'\n'}
          {fileName}
          {'\n'}
          {toBrazilDateTime(new Date())}
        </Text>

        <Text style={styles.filters} fixed>
          Franquia:
          {' '}
          {filters.subsidiary}
          {'\n'}
          Período:
          {' '}
          {filters.period}
        </Text>

        <View style={styles.table}>
          <View style={styles.tableRow}>
            {rowKeys.map((key) => (
              <View style={[styles.colHeader, { width: widths[key] || '10%' }]} key={`header-${key}`}>
                <Text style={styles.tableCellHeader}>{key}</Text>
              </View>
            ))}
          </View>
          {data.map((row) => (
            <View style={styles.tableRow} key={`row-${row.Data}-${row.Cliente}-${row.Condutor}`} wrap={false}>
              {Object.entries(row).map(([key, value]) => (
                <View
                  style={[styles.col, { width: widths[key] || '10%' }]}
                  key={`cell-${row.Data}-${row.Cliente}-${key}`}
                >
                  <Text style={styles.tableCell}>{String(value)}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        <Text style={styles.total} fixed>
          {`TOTAL: ${totalRecords} chamada${totalRecords !== 1 ? 's' : ''}`}
          {truncated ? ' (PDF limitado aos primeiros registros; use Excel/CSV para a lista completa)' : ''}
        </Text>

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => (
            `${pageNumber} / ${totalPages}`
          )}
          fixed
        />
      </Page>
    </Document>
  );
}
